/**
 * Durable case-metadata storage, browser-side. Scope: `CaseManifest` records
 * (which subject, which pipeline stages, paths, timestamps) — NOT the
 * computed pipeline outputs themselves (registered volumes, segmentation
 * masks, reports), which still live only in `wasm-engine/engine.ts`'s
 * in-memory `outputStore` and are lost on reload. Reopening a saved case
 * restores what it *was* (so a run can be repeated without re-entering
 * subject/path/stage settings) — it does not skip re-running stages.
 *
 * No native equivalent exists to port: the real CLI pipelines are stateless,
 * re-reading inputs from disk each invocation. "Project save/load" is a
 * web-app-specific need (no filesystem to fall back on), not a missing
 * pipeline step.
 *
 * Plain `indexedDB`, no dependency — this project keeps its dependency
 * surface deliberately small.
 */
import type { CaseManifest, RecentCase } from "@/ipc/contract";

const DB_NAME = "openads-projects";
const STORE = "cases";
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "caseId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("Could not open the project database."));
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("Project database request failed."));
    });
  } finally {
    db.close();
  }
}

/** Persists (or updates) a case's manifest. Called explicitly — not every `createCaseWeb` autosaves. */
export async function saveCaseRecord(manifest: CaseManifest): Promise<CaseManifest> {
  const updated: CaseManifest = { ...manifest, modifiedIso: new Date().toISOString() };
  await withStore("readwrite", (s) => s.put(updated));
  return updated;
}

export async function loadCaseRecord(caseId: string): Promise<CaseManifest | null> {
  const result = await withStore<CaseManifest | undefined>("readonly", (s) => s.get(caseId));
  return result ?? null;
}

export async function deleteCaseRecord(caseId: string): Promise<void> {
  await withStore("readwrite", (s) => s.delete(caseId));
}

/** Newest first. `exists`/`status` are web-build-appropriate: a saved case
 *  always "exists" (it's the record itself, not a disk path that could have
 *  moved), and status reflects only whether it was ever run, since job
 *  results aren't persisted — see module header. */
export async function listCaseRecords(): Promise<RecentCase[]> {
  const db = await openDb();
  try {
    const all = await new Promise<CaseManifest[]>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result as CaseManifest[]);
      req.onerror = () => reject(req.error ?? new Error("Could not list saved projects."));
    });
    return all
      .sort((a, b) => b.modifiedIso.localeCompare(a.modifiedIso))
      .map(
        (m): RecentCase => ({
          caseId: m.caseId,
          subjectId: m.subjectId,
          studyName: m.studyName,
          caseJsonPath: `indexeddb://${DB_NAME}/${m.caseId}`,
          pipelineKind: m.pipelineKind,
          modality: m.pipelineKind,
          lastModifiedIso: m.modifiedIso,
          status: "not_created",
          exists: true,
        }),
      );
  } finally {
    db.close();
  }
}
